import React from 'react';
import { useQuery } from 'react-query';
import { 
  FileText, 
  Users, 
  User, 
  Clock,
  Search,
  Eye,
  Upload,
  Cog
} from 'lucide-react';
import api from '../services/api';

interface DashboardData {
  total_documents?: number;
  total_users?: number;
  active_users?: number;
  recent_logins?: number;
}

const Dashboard: React.FC = () => {
  const { data: dashboardData = {} } = useQuery<DashboardData>('dashboard', () =>
    api.get('/api/admin/dashboard').then(res => res.data)
  );

  const stats = [
    {
      name: 'Total Documents',
      value: dashboardData?.total_documents || 0,
      icon: FileText,
      color: 'bg-blue-500',
    },
    {
      name: 'Total Users',
      value: dashboardData?.total_users || 0,
      icon: Users,
      color: 'bg-green-500',
    },
    {
      name: 'Active Users',
      value: dashboardData?.active_users || 0,
      icon: User,
      color: 'bg-yellow-500',
    },
    {
      name: 'Recent Logins',
      value: dashboardData?.recent_logins || 0,
      icon: Clock,
      color: 'bg-purple-500',
    },
  ];

  const quickActions = [
    {
      name: 'Search Documents',
      description: 'Find documents by keywords, filters, and advanced search',
      icon: Search,
      href: '/search',
      color: 'bg-blue-500',
    },
    {
      name: 'View Documents',
      description: 'Browse and manage all documents in the system',
      icon: Eye,
      href: '/documents',
      color: 'bg-green-500',
    },
    {
      name: 'Upload Documents',
      description: 'Add new documents to the system',
      icon: Upload,
      href: '/documents/upload',
      color: 'bg-yellow-500',
    },
    {
      name: 'Manage Users',
      description: 'View and manage user accounts and permissions',
      icon: Cog,
      href: '/admin/users',
      color: 'bg-purple-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Welcome to MNA Associates Document Management
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">
          Manage your documents, search efficiently, and collaborate with your team.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div
            key={stat.name}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {stat.name}
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {stat.value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action) => (
            <a
              key={action.name}
              href={action.href}
              className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center">
                <div className={`p-2 rounded-lg ${action.color}`}>
                  <action.icon className="h-5 w-5 text-white" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    {action.name}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {action.description}
                  </p>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
